import { Instant, LocalDate, Month } from "@js-joda/core";
import * as React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { deepEqual } from "../deep-equal";
import { isLoading } from "../Loading";
import * as messages from "../messages";
import { PatientTaskInstance } from "../messages";
import {
  AgencyId,
  ImportedVisitId,
  OnboardingVisitId,
  PatientTaskInstanceId,
  VisitBroadcastId,
  VisitInstanceId,
} from "../messages/ObjectIds";
import { PatientTaskBroadcastDetails } from "../messages/PatientTasks";
import { CaregiverVisitEditRequest } from "../messages/ScheduledVisitEditRequest";
import {
  ClockedInStatus,
  ClockInOutCallType,
  VisitInstanceClockinClockoutRequestParams,
} from "../messages/Visit";
import { AgencyWithChatOnlyDetails } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { AppScheduledVisitsState } from "../system/AppState/AppScheduledVisitsState";
import { Visit } from "../system/AppState/AppVisitsState";
import { HashMap } from "../utils/HashMap";
import { List } from "../utils/List";
import { CicoModalEntity, getClosestVisit } from "../utils/visitUtils";
import { AssignedVisitsAndTasksList } from "./AssignedVisitsAndTasksList";
import { AvailableVisitAndTasksList, getVisitStatus } from "./AvailableVisitsAndTasksList";
import { AvailableVisitsLiveMap } from "./AvailableVisitsLiveMap";
import { ClockActionBanner, ClockActionBannerData } from "./ClockActionBanner";
import ComplianceBanner, { ComplianceBannerProps } from "./ComplianceBanner/ComplianceBanner";
import { If } from "./Generic/If";
import { VisitSort, VisitsTab } from "./Main/MainTypes";
import { UseContactSupportReturnType } from "./useContactSupport";
import { VisitPendingConfirmationBannerList } from "./VisitPendingConfirmation/VisitPendingConfirmationBannerList";

export type SortState = "START_DATE" | "PRICE" | "DISTANCE" | "POSTED_DATE" | "DEFAULT";
export type ScheduleViewState = "DATE" | "LIST";
export type VisitViewState = "LIVEMAP" | "LIST";

export interface VisitsScreenProps {
  clockActionBannerProps: ClockActionBannerData | null;
  clockedInStatus: ClockedInStatus;
  scheduledVisits: AppScheduledVisitsState;
  scrollToScheduleDate?: Instant;
  caregiver: AppCaregiver;
  visits: List<Visit>;
  patientTaskBroadcasts: List<PatientTaskBroadcastDetails>;
  patientTasks: List<PatientTaskInstance>;
  importedVisits: List<messages.CaregiverImportedVisitDetails>;
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails>;
  rescheduleRequests: List<CaregiverVisitEditRequest>;
  activeTab: VisitsTab;
  sortBy: VisitSort;
  isMapOpen: boolean;
  scheduleYear: number;
  scheduleMonth: Month;
  contactSupportProps: UseContactSupportReturnType;
  finishOnboardingSection: React.ReactNode;
  complianceBannerProps: ComplianceBannerProps | null;
  getAnomalies: () => Promise<string[]>;
  onAvailableVisitClick: (visitBroadcastId: VisitBroadcastId) => void;
  onConfirmAssignmentClick: (visitId: VisitBroadcastId, isReviewOnly: boolean) => void;
  onOnboardingVisitClick: (onboardingVisitId: OnboardingVisitId) => void;
  onAvailableTaskClick: (taskBroadcastId: PatientTaskInstanceId) => void;
  onScheduledVisitClick: (visitInstanceId: VisitInstanceId) => void;
  onImportedVisitClick: (importedVisitId: ImportedVisitId, patientName: string) => void;
  onRequestNavigate: (page: Page) => void;
  onRequestAvailableVisits: () => void;
  onClickTask: (
    patientTaskInstanceId: PatientTaskInstanceId,
    initialScheduleDateTimePickerOpen: boolean
  ) => void;
  onCaseSettingsNavigationClick: () => void;
  onVisitCardViewed: (visitIds: VisitBroadcastId[]) => void;
  onVisitMoreDetailsViewed: (visitId: VisitBroadcastId) => void;
  onClickClockedInBanner: (details: VisitInstanceId) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onClickTab: (tab: VisitsTab) => void;
  onClickStartNow: (entity: CicoModalEntity) => void;
  onOpenMap: () => void;
  onUploadVerificationSelfie: (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ) => Promise<void>;
  onRequestVisitAdjustmentCheck: API["getCaregiverCanAdjustVisitInstanceTimes"];
  onClickNextMonth: () => void;
  onCheckIfDocumentsSubmitted: API["checkIfAllDocumentsSubmitted"];
  onCaregiverMarkVisitAsMissed: API["caregiverMarkVisitAsMissed"];
}

export interface VisitsScreenState {
  scheduleView: ScheduleViewState;
  mapChangedOnce: boolean;
}

let isChanged: boolean = false;

export class VisitsScreen extends React.Component<VisitsScreenProps, VisitsScreenState> {
  constructor(props: VisitsScreenProps) {
    super(props);

    this.state = {
      mapChangedOnce: false,
      scheduleView: "DATE",
    };

    if (!this.state.mapChangedOnce && this.props.caregiver.isCaregiverOfMedFlyt) {
      this.props.onOpenMap();
      this.state = {
        mapChangedOnce: true,
        scheduleView: "DATE",
      };
    }
  }

  private didScrollToNotification: boolean = false;

  componentDidUpdate() {
    if (this.props.activeTab === "Schedule") {
      this.handleJumpToScheduleDate();
    }
  }

  shouldComponentUpdate(nextProps: VisitsScreenProps) {
    return (
      this.checkIsChanged() ||
      !deepEqual(nextProps.caregiver, this.props.caregiver) ||
      !deepEqual(nextProps.visits, this.props.visits) ||
      !deepEqual(nextProps.complianceBannerProps, this.props.complianceBannerProps) ||
      nextProps.patientTaskBroadcasts !== this.props.patientTaskBroadcasts ||
      nextProps.agencies !== this.props.agencies ||
      !deepEqual(nextProps.scheduledVisits, this.props.scheduledVisits) ||
      this.props.scheduleYear !== nextProps.scheduleYear ||
      this.props.scheduleMonth !== nextProps.scheduleMonth ||
      this.props.importedVisits !== nextProps.importedVisits ||
      this.props.patientTasks !== nextProps.patientTasks ||
      this.props.activeTab !== nextProps.activeTab ||
      this.props.sortBy !== nextProps.sortBy ||
      this.props.isMapOpen !== nextProps.isMapOpen
    );
  }

  checkIsChanged() {
    const result = isChanged;
    if (isChanged) {
      isChanged = false;
    }

    return result;
  }

  handleJumpToScheduleDate() {
    if (this.props.scrollToScheduleDate !== undefined && !this.didScrollToNotification) {
      this.jumpToElement(
        "day" + this.props.scrollToScheduleDate.toString().substring(0, 10),
        "smooth"
      );
      this.didScrollToNotification = true;
    } else {
      const closestVisit = getClosestVisit(this.props.scheduledVisits.scheduledVisitDetails);
      const date = closestVisit?.data.startTimeLocal.toLocalDate() ?? LocalDate.now();

      this.jumpToElement("day" + date.toString(), "smooth");
    }
  }

  jumpToElement(id: string, behavior: "instant" | "smooth") {
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element !== null) {
        element.scrollIntoView({
          block: "start",
          behavior: behavior as any,
        });
      }
    }, 50);
  }

  handleViewChange() {
    isChanged = true;

    // If switching from list (to date)
    if (this.state.scheduleView === "LIST") {
      this.jumpToElement("day" + LocalDate.now().toString(), "smooth");
    } else {
      this.jumpToElement("visitsTop", "instant");
    }
    this.setState({
      scheduleView: this.state.scheduleView === "DATE" ? "LIST" : "DATE",
    });
  }

  getVisitById(visitId: VisitBroadcastId | null) {
    if (!isLoading(this.props.visits) && visitId !== null) {
      for (const visit of this.props.visits) {
        if (visit.id === visitId) {
          return visit;
        }
      }
    }

    return null;
  }

  getTasksCount(): number {
    const currentMonth = this.props.scheduleMonth.value();
    const currentYear = this.props.scheduleYear;
    const tasks = this.props.patientTasks;
    return tasks
      .filter((task) => {
        return (
          (task.startDate.month().value() === currentMonth &&
            task.startDate.year() === currentYear) ||
          (task.dueDate.month().value() === currentMonth && task.dueDate.year() === currentYear)
        );
      })
      .length();
  }

  render() {
    const availableVisits = this.props.visits.filter(
      ({ type }) => type === "Available"
    ) as List<Visit.Available>;

    const pendingVisits = availableVisits.filter(
      (visit) => getVisitStatus(visit) === "PENDING_CONFIRMATION"
    );

    const onPendingConfirmationVisitClick = (visitId: VisitBroadcastId) => {
      return this.props.onConfirmAssignmentClick(visitId, false);
    };

    return (
      <>
        <div
          style={{
            overflowY:
              this.props.isMapOpen && this.props.activeTab === "Available" ? "hidden" : "scroll",
          }}
          id="VisitsScreen__contentCont"
          className="VisitsScreen__contentCont"
        >
          <If
            condition={
              this.props.caregiver.isCaregiverOfMedFlyt &&
              ((!this.props.isMapOpen && this.props.activeTab === "Available") ||
                !(this.props.activeTab === "Available"))
            }
          >
            <>
              <div className="VisitsScreen__AvailableVisitListBannersSection">
                <VisitScreenTabsMedflytBanner
                  finishOnboardingSection={this.props.finishOnboardingSection}
                  clockActionBannerProps={this.props.clockActionBannerProps}
                  onClickClockedInBanner={this.props.onClickClockedInBanner}
                />
              </div>
              {this.props.complianceBannerProps !== null ? (
                <ComplianceBanner {...this.props.complianceBannerProps} />
              ) : null}
            </>
          </If>
          <If condition={!this.props.caregiver.isCaregiverOfMedFlyt}>
            <div className="VisitsScreen__AvailableVisitListBannersSection">
              {this.props.clockActionBannerProps !== null ? (
                <ClockActionBanner
                  {...this.props.clockActionBannerProps}
                  onClick={this.props.onClickClockedInBanner}
                />
              ) : null}
            </div>
            {this.props.finishOnboardingSection}
            {this.props.caregiver.shouldShowComplianceBanner &&
            this.props.complianceBannerProps !== null ? (
              <ComplianceBanner {...this.props.complianceBannerProps} />
            ) : null}
          </If>
          <div id="visitsTop">
            <div
              className={this.props.activeTab === "Available" ? "" : "VisitsScreen__hiddenTab"}
              style={{ height: "100%" }}
            >
              {!(this.props.caregiver.isCaregiverOfMedFlyt && this.props.isMapOpen) ? (
                <div className="VisitsScreen__PendingConfirmationBanner">
                  <VisitPendingConfirmationBannerList
                    visits={pendingVisits}
                    onConfirmAssignmentClick={this.props.onConfirmAssignmentClick}
                  />
                </div>
              ) : null}
              {!this.props.isMapOpen ? (
                <AvailableVisitAndTasksList
                  sortBy={this.props.sortBy}
                  agencies={this.props.agencies}
                  caregiver={this.props.caregiver}
                  availableVisits={this.props.visits}
                  availableTaskBroadcasts={this.props.patientTaskBroadcasts}
                  getAnomalies={this.props.getAnomalies}
                  onVisitClick={(visitId) => this.props.onAvailableVisitClick(visitId)}
                  onOnboardingVisitClick={(visitId) => this.props.onOnboardingVisitClick(visitId)}
                  onCaseSettingsNavigationClick={this.props.onCaseSettingsNavigationClick}
                  onAvailableVisitCardViewed={this.props.onVisitCardViewed}
                  onVisitMoreDetailsViewed={this.props.onVisitMoreDetailsViewed}
                  onAvailableTaskClick={this.props.onAvailableTaskClick}
                  onPendingConfirmationVisitClick={onPendingConfirmationVisitClick}
                />
              ) : (
                <AvailableVisitsLiveMap
                  caregiver={this.props.caregiver}
                  visits={this.props.visits}
                  finishOnboardingSection={this.props.finishOnboardingSection}
                  clockActionBannerProps={this.props.clockActionBannerProps}
                  complianceBannerProps={this.props.complianceBannerProps}
                  visitsForPendingBanner={pendingVisits}
                  onConfirmAssignmentClick={this.props.onConfirmAssignmentClick}
                  onClickClockedInBanner={this.props.onClickClockedInBanner}
                  onOnboardingVisitClick={(visitId) => this.props.onOnboardingVisitClick(visitId)}
                  onVisitClick={(visitId) => this.props.onAvailableVisitClick(visitId)}
                  currentSection={this.props.activeTab}
                  onCaseSettingsNavigationClick={this.props.onCaseSettingsNavigationClick}
                  onPendingConfirmationVisitClick={onPendingConfirmationVisitClick}
                />
              )}
            </div>
            <div
              style={{
                display: this.props.activeTab === "Schedule" ? "block" : "none",
                height: "100%",
              }}
            >
              <AssignedVisitsAndTasksList
                caregiverId={this.props.caregiver.id}
                isCaregiverOfMedflyt={this.props.caregiver.isCaregiverOfMedFlyt}
                clockedInStatus={this.props.clockedInStatus}
                visitInstances={this.props.scheduledVisits.scheduledVisitDetails}
                rescheduleRequests={this.props.rescheduleRequests}
                importedVisits={this.props.importedVisits}
                patientTasksInstances={this.props.patientTasks}
                contactSupportProps={this.props.contactSupportProps}
                onScheduledVisitClick={this.props.onScheduledVisitClick}
                onImportedVisitClick={this.props.onImportedVisitClick}
                onRequestNavigate={this.props.onRequestNavigate}
                onRequestAvailableVisits={this.props.onRequestAvailableVisits}
                onClickTask={this.props.onClickTask}
                scheduleView={this.state.scheduleView}
                scheduleViewMonth={this.props.scheduleMonth}
                scheduleViewYear={this.props.scheduleYear}
                onClock={this.props.onClock}
                onClickStartNow={this.props.onClickStartNow}
                onUploadVerificationSelfie={this.props.onUploadVerificationSelfie}
                onRequestVisitAdjustmentCheck={this.props.onRequestVisitAdjustmentCheck}
                onClickNextMonth={this.props.onClickNextMonth}
                onCheckIfDocumentsSubmitted={this.props.onCheckIfDocumentsSubmitted}
                onCaregiverMarkVisitAsMissed={this.props.onCaregiverMarkVisitAsMissed}
              />
            </div>
          </div>
        </div>
      </>
    );
  }
}

export const VisitScreenTabsMedflytBanner = (props: {
  clockActionBannerProps: ClockActionBannerData | null;
  finishOnboardingSection: React.ReactNode;
  onClickClockedInBanner: (details: messages.VisitInstanceId) => void;
}) => {
  return (
    <>
      {props.clockActionBannerProps !== null ? (
        <ClockActionBanner
          {...props.clockActionBannerProps}
          onClick={props.onClickClockedInBanner}
        />
      ) : (
        props.finishOnboardingSection
      )}
    </>
  );
};
